import { PointArray } from '@editor/interfaces/attribute'
import { InspectionItem } from '@editor/interfaces/inspection'
import { PendingJobResponse } from '@editor/interfaces/system'
import { API_GATEWAY_URL } from '@infraloop/services'
import { Vector3 } from 'three'

import { ERROR_PROCESS } from './ErrorHandler'
import { withStandardHandlers } from './Util'

/**
 * Calculate the midpoint of a polyline defined by an array of points.
 *
 * @param allPoints - An array of points representing the polyline.
 * @returns An object containing the total distance of the polyline and the coordinates of the midpoint.
 */
export const calculatePolylineMidpoint = (
  allPoints: (PointArray | Vector3)[],
): { totalDistance: number; point: PointArray } => {
  const allPointsV3 = allPoints.map((p) => (p instanceof Vector3 ? p.clone() : new Vector3().fromArray(p)))

  // Go through each section of the polyline and calculate the distance between them, then the total distance.
  const totalDistance = allPointsV3.reduce<number>((collection, point, index) => {
    const pointsPair = [point, allPointsV3[index + 1]]
    if (!pointsPair[1]) return collection
    return collection + pointsPair[0].distanceTo(pointsPair[1])
  }, 0)

  // Handle case where all points are duplicates (zero total distance)
  if (totalDistance === 0) {
    return {
      totalDistance: 0,
      point: allPointsV3[0].toArray(),
    }
  }

  // Figure out the mid-point of the polyline.
  const midPointDistance = totalDistance / 2
  return {
    totalDistance,
    point: allPointsV3.reduce<{ point: PointArray; totalDistance: number }>(
      (collection, point, index) => {
        if (collection.totalDistance >= midPointDistance) return collection

        const pointsPair = [point, allPointsV3[index + 1]]
        if (!pointsPair[1]) return collection

        // Skip duplicate points (zero distance)
        const distance = pointsPair[0].distanceTo(pointsPair[1])
        if (distance === 0) return collection

        if (collection.totalDistance + distance < midPointDistance) {
          collection.totalDistance += distance
          return collection
        }

        const remainingDistance = midPointDistance - collection.totalDistance
        const result = pointsPair[0].lerp(pointsPair[1], remainingDistance / distance).toArray()

        return { point: result, totalDistance: midPointDistance }
      },
      {
        point: [0, 0, 0],
        totalDistance: 0,
      },
    ).point,
  }
}

/**
 * Calculate the total length of a polyline defined by an array of points.
 *
 * @param allPoints - An array of points representing the polyline.
 */
export const calculatePolylineLength = (allPoints: (PointArray | Vector3)[]): number => {
  const allPointsV3 = allPoints.map((p) => (p instanceof Vector3 ? p.clone() : new Vector3().fromArray(p)))

  // Go through each section of the polyline and calculate the distance between them, then the total distance.
  const totalDistance = allPointsV3.reduce<number>((collection, point, index) => {
    const pointsPair = [point, allPointsV3[index + 1]]
    if (!pointsPair[1]) return collection
    return collection + pointsPair[0].distanceTo(pointsPair[1])
  }, 0)

  return totalDistance
}

/**
 * Calculate the edge index in a polyline given a point on the line.
 *
 * @param allPoints - An array of points representing the polyline.
 * @param point - The position on the polyline to calculate the index for.
 */
export const calculateIndexInPolyline = (allPoints: (PointArray | Vector3)[], point: PointArray | Vector3): number => {
  const allPointsV3 = allPoints.map((p) => (p instanceof Vector3 ? p.clone() : new Vector3().fromArray(p)))
  const pointV3 = point instanceof Vector3 ? point.clone() : new Vector3().fromArray(point)

  let closestIndex = -1
  let closestDistance = Infinity

  // Go through each segment of the polyline to find the closest point on the polyline to the given point
  allPointsV3.forEach((startPoint, index) => {
    const endPoint = allPointsV3[index + 1]
    if (!endPoint) return

    // Project the point onto the line segment
    const lineVec = endPoint.clone().sub(startPoint)
    const pointVec = pointV3.clone().sub(startPoint)
    const lineLenSq = lineVec.lengthSq()
    const t = Math.max(0, Math.min(1, pointVec.dot(lineVec) / lineLenSq))
    const projection = startPoint.clone().add(lineVec.multiplyScalar(t))

    const distance = projection.distanceTo(pointV3)
    if (distance < closestDistance) {
      closestDistance = distance
      closestIndex = index + 1 // after the startPoint
    }
  })

  return closestIndex
}

/**
 * Split a polyline at a given index into two separate polylines.
 *
 * @param allPoints - An array of points representing the polyline.
 * @param index - The index at which to split the polyline.
 * @returns An array containing two arrays of points, representing the two new polylines.
 */
export const splitPolylineAtIndex = <T>(allPoints: T[], index: number): [T[], T[]] | null => {
  if (index <= 0 || index >= allPoints.length) {
    return null
  }

  const firstPart = allPoints.slice(0, index)
  const secondPart = allPoints.slice(index)

  return [firstPart, secondPart]
}

/**
 * Detect polyline width using crack detection results.
 * @param accessToken Auth0 access token
 * @param projectId Project ID
 * @param inspectionAreaId Inspection Area ID
 * @param inspectionSheetId Inspection Sheet ID
 * @param polylines Polylines to process
 * @param pathEntwine S3 path to the crack detection result
 */
export const detectPolylineWidth = async (
  accessToken: string,
  projectId: string,
  inspectionAreaId: string,
  inspectionSheetId: string,
  polylines: Partial<InspectionItem>[],
  pathEntwine: string,
) => {
  const jobRequest = () =>
    fetch(`${API_GATEWAY_URL}/save-polylines-with-width-detection`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        responseType: 'json',
        'X-Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        project_id: projectId,
        inspection_area_id: inspectionAreaId,
        inspection_sheet_id: inspectionSheetId,
        path_entwine_crack_detection_result: pathEntwine,
        polylines,
      }),
    })

  return withStandardHandlers<typeof jobRequest, PendingJobResponse>(
    jobRequest,
    ERROR_PROCESS.MODIFY_INSPECTION_SHEET,
  )()
}
